import React from 'react';
import { CheckCircle, Clock, AlertCircle, Upload } from 'lucide-react';

interface UploadStatusProps {
  isUploading: boolean;
  progress: number;
  error: string | null;
  uploadResults: Array<{
    originalFileName: string;
    uniqueFileName: string;
    uploadUrl: string;
  }>;
}

const UploadStatus: React.FC<UploadStatusProps> = ({ 
  isUploading, 
  progress, 
  error, 
  uploadResults 
}) => {
  if (!isUploading && !error && uploadResults.length === 0) {
    return null;
  }

  const getStatusIcon = (): React.ReactNode => {
    if (error) {
      return <AlertCircle className="h-6 w-6 text-red-500" />;
    }
    if (isUploading) {
      return <Clock className="h-6 w-6 text-blue-500 animate-pulse" />;
    }
    return <CheckCircle className="h-6 w-6 text-green-500" />;
  };

  const getStatusText = (): string => {
    if (error) {
      return 'Upload failed';
    }
    if (isUploading) {
      return 'Uploading files...';
    }
    return 'Upload completed successfully!';
  };

  const getStatusColor = (): string => {
    if (error) {
      return 'text-red-600';
    }
    if (isUploading) {
      return 'text-blue-600';
    }
    return 'text-green-600';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-4">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className={`text-lg font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </h3>
          
          {isUploading && (
            <div className="mt-2">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {uploadResults.length > 0 && !isUploading && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Uploaded files:</p>
              <div className="space-y-2">
                {uploadResults.map((result, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                    <Upload className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-gray-700">{result.originalFileName}</span>
                    <span className="text-xs text-gray-500">→ {result.uniqueFileName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadStatus;
